import { Component, inject, OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { AuthService, GenericError } from '@auth0/auth0-angular';
import { filter, switchMap, take } from 'rxjs';
import { UserService } from './api';
import { TiveSessionService } from './auth/tive-session.service';
import { WarningBannerComponent } from './shared/warning-banner/warning-banner';
import { TypeformLoadDirective } from './shared/typeform-load.directive';
import { environment } from '../environments/environment';

/**
 * Auth0 error codes that indicate the session is unrecoverable and the user
 * must re-authenticate from scratch (logout → login).
 */
const UNRECOVERABLE_AUTH_ERRORS = new Set(['missing_refresh_token', 'login_required']);

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, AsyncPipe, WarningBannerComponent, TypeformLoadDirective],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  private auth = inject(AuthService);
  private userService = inject(UserService);
  protected session = inject(TiveSessionService);
  protected platformUrl = environment.platformUrl;

  isAuthenticated$ = this.auth.isAuthenticated$;
  isLoading$ = this.auth.isLoading$;

  login() {
    this.auth.loginWithRedirect({
      appState: { target: window.location.pathname + window.location.search },
    });
  }

  logout() {
    this.session.clear();
    this.auth.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }

  retry() {
    window.location.reload();
  }

  ngOnInit() {
    // Catch SDK-level auth errors (e.g. expired refresh token detected during
    // initialization, before any API call is attempted).
    this.auth.error$.pipe(filter((err) => this.isUnrecoverableAuthError(err))).subscribe((err) => {
      console.warn('[app] Auth0 SDK error — logging out to allow fresh login:', err.message);
      this.logout();
    });

    // When authenticated, call loginByEmail to initialize the Tive session.
    // This was previously in AccountsList.ngOnInit() but must run once at
    // the app level so the session is ready regardless of which page loads first.
    this.auth.isAuthenticated$
      .pipe(
        filter((isAuth) => isAuth),
        take(1),
        switchMap(() => this.auth.user$),
        filter((profile) => !!profile?.email),
        take(1),
        switchMap((profile) =>
          this.userService.apiUserLoginByEmailPost(undefined, JSON.stringify(profile!.email)),
        ),
      )
      .subscribe({
        next: (data) => {
          this.session.setLoginResponse(data);
        },
        error: (err) => {
          if (this.isUnrecoverableAuthError(err)) {
            console.warn(
              '[app] Unrecoverable auth error during session init — logging out:',
              err.message,
            );
            this.logout();
            return;
          }
          this.session.setInitError(err?.message ?? 'Failed to initialize session');
        },
      });
  }

  /**
   * Detect auth errors where the only recovery path is a full re-login.
   * These include expired/missing refresh tokens and "login required" errors
   * from Auth0's token endpoint.
   */
  private isUnrecoverableAuthError(err: unknown): boolean {
    if (err instanceof GenericError) {
      return UNRECOVERABLE_AUTH_ERRORS.has(err.error);
    }
    return false;
  }
}
